import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAdminMFA, MFAStatus } from "@/hooks/useAdminMFA";
import { AdminMFASetup } from "./AdminMFASetup";
import { AdminMFAVerify } from "./AdminMFAVerify";

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const { status: mfaStatus, isChecking: mfaChecking, refreshStatus } = useAdminMFA();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setChecking(false);
        return;
      }

      try {
        // Check if user has any admin role using has_any_admin_role function
        const { data, error } = await supabase
          .rpc('has_any_admin_role', { _user_id: user.id });

        if (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
        } else {
          setIsAdmin(data === true);
        }
      } catch (error) {
        console.error("Error in admin check:", error);
        setIsAdmin(false);
      } finally {
        setChecking(false);
      }
    };

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!checking && isAdmin === false) {
      toast.error("Accès non autorisé");
      navigate("/dashboard");
    }
  }, [checking, isAdmin, navigate]);

  // Show loading while checking auth or admin status
  if (authLoading || checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Vérification des accès...</p>
        </div>
      </div>
    );
  }

  // Not logged in or not admin
  if (!user || !isAdmin) {
    return null;
  }

  // Show loading while checking MFA status
  if (mfaChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Vérification de la sécurité...</p>
        </div>
      </div>
    );
  }

  // MFA not enrolled - force setup
  if (mfaStatus === "not_enrolled") {
    return <AdminMFASetup />;
  }

  // MFA enrolled but needs verification
  if (mfaStatus === "needs_verification") {
    return <AdminMFAVerify onVerified={refreshStatus} />;
  }

  // All checks passed - render admin content
  return <>{children}</>;
}
